// File Metadata:
// Filename: AddCrewDialog.js
// Version: 1.00
// Created: 2025-03-30 15:39:06
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { 
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Stack, TextField, Select, FormControl,
    InputLabel, MenuItem, Box, Typography, Alert
} from '@mui/material'
//import { useAddCrewMutation } from '../../../api/crewsApiSlice'


const CrewDialog = ({ open, onClose, crew, onSave }) => {
    //const [addCrew] = useAddCrewMutation()
    const [error, setError] = useState('')
    
    // Separate form state for dialog
    const [dialogFormData, setDialogFormData] = useState({
        crewId: crew?.crewId || '',  // Change 'id' to 'crewId'
        name: crew?.name || '',
        type: crew?.type || 'weekday',
        schedule: crew?.schedule || {
            type: 'fixed',
            fixed: {
                hours: { start: '', end: '' }
            },
            alternating: {
                evenWeeks: { hours: { start: '', end: '' } },
                oddWeeks: { hours: { start: '', end: '' } }
            },
            breaks: [{
                start: '',
                end: '',
                duration: 30,
                paid: false
            }]
        }
    })

    // Initialize form when dialog opens
    useEffect(() => {
        if (crew) {
            setDialogFormData(crew)
        }
    }, [crew])

    const validateForm = () => {
        if (!dialogFormData.crewId) {
            setError('Crew ID is required')
            return false
        }
        if (!/^[1-9][0-9]{2}$/.test(dialogFormData.crewId)) {
            setError('Crew ID must be between 100-999')
            return false
        }
        if (!dialogFormData.name?.trim()) {
            setError('Crew name is required')
            return false
        }
        
        // Validate schedule based on type
        if (dialogFormData.schedule.type === 'fixed') {
            if (!dialogFormData.schedule.fixed.hours.start || !dialogFormData.schedule.fixed.hours.end) {
                setError('Fixed schedule requires start and end times')
                return false
            }
        }

        setError('')
        return true
    }

    const handleSubmit = async () => {
        if (!validateForm()) return

        try {
            console.log('Submitting crew data:', dialogFormData)
            //await addCrew(dialogFormData).unwrap()
            onSave(dialogFormData)  // Call onSave with the form data
            onClose()
        } catch (err) {
            console.error('Error adding crew:', err) // Add error logging
            setError(err.data?.message || 'Failed to add crew')
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{crew ? 'Edit Crew' : 'Add New Crew'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}
                    <TextField
                        required
                        label="Crew ID"
                        value={dialogFormData.crewId}  // Update field name
                        onChange={(e) => setDialogFormData(prev => ({ 
                            ...prev, 
                            crewId: e.target.value 
                        }))}
                        helperText="Crew number (100-599)"
                        error={!!error && error.includes('Crew ID')}
                    />
                    <TextField
                        required
                        label="Crew Name"
                        value={dialogFormData.name}
                        onChange={(e) => setDialogFormData(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Crew Type</InputLabel>
                        <Select
                            value={dialogFormData.type}
                            label="Crew Type"
                            onChange={(e) => setDialogFormData(prev => ({ ...prev, type: e.target.value }))}
                        >
                            <MenuItem value="weekday">Weekday</MenuItem>
                            <MenuItem value="weekend">Weekend</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Schedule Type</InputLabel>
                        <Select
                            value={dialogFormData.schedule.type}
                            label="Schedule Type"
                            onChange={(e) => setDialogFormData(prev => ({
                                ...prev,
                                schedule: {
                                    ...prev.schedule,
                                    type: e.target.value
                                }
                            }))}
                        >
                            <MenuItem value="fixed">Fixed</MenuItem>
                            <MenuItem value="alternating">Alternating</MenuItem>
                        </Select>
                    </FormControl>

                    {dialogFormData.schedule.type === 'fixed' ? (
                        <Box>
                            <Typography variant="subtitle2">Fixed Schedule</Typography>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Start Time"
                                    type="time"
                                    value={dialogFormData.schedule.fixed.hours.start}
                                    onChange={(e) => setDialogFormData(prev => ({
                                        ...prev,
                                        schedule: {
                                            ...prev.schedule,
                                            fixed: {
                                                hours: {
                                                    ...prev.schedule.fixed.hours,
                                                    start: e.target.value
                                                }
                                            }
                                        }
                                    }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="End Time"
                                    type="time"
                                    value={dialogFormData.schedule.fixed.hours.end}
                                    onChange={(e) => setDialogFormData(prev => ({
                                        ...prev,
                                        schedule: {
                                            ...prev.schedule,
                                            fixed: {
                                                hours: {
                                                    ...prev.schedule.fixed.hours,
                                                    end: e.target.value
                                                }
                                            }
                                        }
                                    }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Stack>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="subtitle2">Alternating Schedule</Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption">Even Weeks</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            value={dialogFormData.schedule.alternating.evenWeeks.hours.start}
                                            onChange={(e) => setDialogFormData(prev => ({
                                                ...prev,
                                                schedule: {
                                                    ...prev.schedule,
                                                    alternating: {
                                                        ...prev.schedule.alternating,
                                                        evenWeeks: {
                                                            hours: {
                                                                ...prev.schedule.alternating.evenWeeks.hours,
                                                                start: e.target.value
                                                            }
                                                        }
                                                    }
                                                }
                                            }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            value={dialogFormData.schedule.alternating.evenWeeks.hours.end}
                                            onChange={(e) => setDialogFormData(prev => ({
                                                ...prev,
                                                schedule: {
                                                    ...prev.schedule,
                                                    alternating: {
                                                        ...prev.schedule.alternating,
                                                        evenWeeks: {
                                                            hours: {
                                                                ...prev.schedule.alternating.evenWeeks.hours,
                                                                end: e.target.value
                                                            }
                                                        }
                                                    }
                                                }
                                            }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography variant="caption">Odd Weeks</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            value={dialogFormData.schedule.alternating.oddWeeks.hours.start}
                                            onChange={(e) => setDialogFormData(prev => ({
                                                ...prev,
                                                schedule: {
                                                    ...prev.schedule,
                                                    alternating: {
                                                        ...prev.schedule.alternating,
                                                        oddWeeks: {
                                                            hours: {
                                                                ...prev.schedule.alternating.oddWeeks.hours,
                                                                start: e.target.value
                                                            }
                                                        }
                                                    }
                                                }
                                            }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            value={dialogFormData.schedule.alternating.oddWeeks.hours.end}
                                            onChange={(e) => setDialogFormData(prev => ({
                                                ...prev,
                                                schedule: {
                                                    ...prev.schedule,
                                                    alternating: {
                                                        ...prev.schedule.alternating,
                                                        oddWeeks: {
                                                            hours: {
                                                                ...prev.schedule.alternating.oddWeeks.hours,
                                                                end: e.target.value
                                                            }
                                                        }
                                                    }
                                                }
                                            }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {crew ? 'Update' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

CrewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    crew: PropTypes.object,
    onSave: PropTypes.func.isRequired
}

export default CrewDialog